import {
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  type SVGAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AlertingIcon,
  Box,
  Column,
  IconButton,
  Popover,
  PopoverTrigger,
  Portal,
  ProjectsIcon,
  Spinner,
  Tooltip,
  useDisclosure,
  useMergeRefs,
} from "@hightouch/htui";

import {
  isSidebarShortcut,
  MINI_NAV_SHEET_ATTRIBUTE,
  SIDEBAR_SHORTCUT,
} from "src/components/layout/nav/mini-nav/mini-nav-context";
import { ChatSupport } from "src/components/layout/nav/footer/chat-support";
import { Resources } from "src/components/layout/nav/footer/resources";
import { ContextNavIcon } from "src/components/layout/nav/footer";
import { Icon as SettingsNavIcon } from "src/components/layout/nav/icons/settings";
import {
  type AmpNavLink,
  useAmpNavLinks,
} from "src/components/layout/nav/sections/amp-nav-links";
import { UnreadBadge } from "src/components/layout/nav/sections/projects";
import { CompactNavContext } from "src/components/layout/nav/compact-nav-context";
import { NavHoverProvider } from "src/components/layout/nav/nav-hover";
import { NavItem } from "src/components/layout/nav/nav-item";
import { cachedNavIcon } from "src/components/layout/nav/nav-icon";
import { NavPopoverContentAndBody } from "src/components/layout/nav/nav-popovers";
import { isSubPath } from "src/components/layout/nav/utils";
import { WorkspaceSelector } from "src/components/layout/nav/workspace-selector";
import { NavWidthContext, useNavWidth } from "src/contexts/nav-width-context";
import { isModifiedClick } from "src/pages/ad-studio/insights/opportunities/utils";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { InboxModal } from "src/pages/projects/inbox/inbox-button";
import {
  unreadLabel,
  useInboxUnreadCount,
} from "src/pages/projects/inbox/inbox-queries";
import { useLocation, useNavigate } from "src/router";
import { HightouchMark } from "src/ui/brand";

/** The list's left edge on the title pill's (the pill's 4px inset), 8px below it. */
const LIST_OFFSET: [number, number] = [-4, 12];
const OPENING_KEYS = new Set(["Enter", " "]);
const SWITCH_WORKSPACE = "switch-workspace";

/**
 * The way out of a fullscreen canvas: the AMP nav's pages and controls, on the nav's own rows under the workspace selector.
 * `beforeLeave` keeps an open editor's edits before a same-tab navigation leaves the canvas.
 */
export function CanvasMiniNav({
  beforeLeave,
}: {
  beforeLeave?: () => Promise<boolean>;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollFade = useScrollFade();
  const listRefs = useMergeRefs(listRef, scrollFade.ref);
  // Only a keyboard open lands on the first row.
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  // Focus comes back to the button after a press elsewhere closes the popover, which would show its tooltip.
  const [isTooltipHeld, setIsTooltipHeld] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const unreadCount = useInboxUnreadCount({ isEnabled: true });
  const { lead, area, more, context, settings } = useAmpNavLinks();
  const navigate = useNavigate();
  const isLeaving = useRef(false);
  // The row picked while an open editor saves, so the popover shows the click landed.
  const [leavingTo, setLeavingTo] = useState<string | null>(null);

  // A save that finishes after the user went elsewhere, or left the canvas, must not navigate.
  const currentLocation = useRef<string | null>(null);
  const { key: locationKey } = useLocation();
  useEffect(() => {
    currentLocation.current = locationKey;
    return () => {
      currentLocation.current = null;
    };
  }, [locationKey]);

  // The popover returns focus to its button itself, unless the press moved focus to another field.
  const close = useCallback(() => {
    onClose();
    setIsKeyboardOpen(false);
    setLeavingTo(null);
  }, [onClose]);

  useEffect(() => {
    const toggle = (event: KeyboardEvent) => {
      // The sidebar's own shortcut, which leaves a field, an IME and a dialog over the canvas their keys.
      if (!isSidebarShortcut(event)) return;
      // Capture, ahead of the canvas's own one-key actions.
      event.preventDefault();
      event.stopPropagation();
      const button = buttonRef.current;
      if (!button) return;
      if (button.getAttribute("aria-expanded") !== "true")
        setIsKeyboardOpen(true);
      button.click();
    };
    document.addEventListener("keydown", toggle, true);
    return () => document.removeEventListener("keydown", toggle, true);
  }, []);

  // A keyboard open lands on the first row, a frame on, once the popover can take the focus.
  useEffect(() => {
    if (!isOpen || !isKeyboardOpen) return;
    const frame = requestAnimationFrame(() =>
      listRef.current?.querySelector<HTMLElement>("a[href]")?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [isOpen, isKeyboardOpen]);

  // The canvas's pan and zoom keep presses on it from the popover's own outside-click check.
  useEffect(() => {
    if (!isOpen) return;
    const closeOutside = (event: PointerEvent) => {
      const { target } = event;
      if (!(target instanceof Node)) return;
      const list = listRef.current?.closest('[role="dialog"]');
      if (list?.contains(target) || buttonRef.current?.contains(target)) return;
      // The workspace selector's and Resources' lists render in portals, outside the popover.
      const isInPopover = [
        ...(list?.querySelectorAll('[aria-haspopup="dialog"]') ?? []),
      ].some((trigger) => {
        const id = trigger.getAttribute("aria-controls");
        return id && document.getElementById(id)?.contains(target);
      });
      if (isInPopover) return;
      setIsTooltipHeld(true);
      close();
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () =>
      document.removeEventListener("pointerdown", closeOutside, true);
  }, [isOpen, close]);

  // Runs `go` once an open editor has kept its edits, unless the user went elsewhere meanwhile.
  const leaveWith = (key: string, go: () => void) => {
    if (isLeaving.current) return;
    isLeaving.current = true;
    setLeavingTo(key);
    const from = currentLocation.current;
    const leave = async () => {
      try {
        const isSaved = beforeLeave ? await beforeLeave() : true;
        if (isSaved && from !== null && currentLocation.current === from) go();
      } finally {
        isLeaving.current = false;
      }
      // An editor's unsaved-changes guard can block the navigation and keep the canvas.
      close();
    };
    void leave();
  };

  // A modified or middle click opens a new tab and leaves this one, editor and all, where it is.
  const leaveTo = (href: string) => (event: MouseEvent<HTMLElement>) => {
    if (!beforeLeave || event.button !== 0 || isModifiedClick(event)) {
      close();
      return;
    }
    event.preventDefault();
    leaveWith(href, () => navigate(href));
  };

  // A Resources row opens a page in a new tab or the feedback chat, so the mini-nav steps aside; its trigger only opens the list.
  const closeAfterResource = (event: MouseEvent<HTMLElement>) => {
    if (!(event.target instanceof Element)) return;
    const dialog = event.target.closest('[role="dialog"]');
    const isInResources = dialog !== null && !dialog.contains(listRef.current);
    if (isInResources && event.target.closest("a, button")) close();
  };

  const leaveItem = (
    item: Pick<AmpNavLink, "href" | "label" | "activePaths" | "markerLabel">,
    icon: ComponentProps<typeof NavItem>["icon"],
    extra?: Pick<ComponentProps<typeof NavItem>, "ariaLabel" | "rightElement">,
  ) => (
    // Capture, so a click or Enter on the link itself keeps the open editor's edits first.
    <Box key={item.href} onClickCapture={leaveTo(item.href)}>
      <NavItem
        href={item.href}
        isSub={isSubPath(item.href)}
        activePaths={item.activePaths}
        icon={leavingTo === item.href ? SavingIcon : icon}
        label={item.label}
        markerLabel={item.markerLabel}
        // The canvas lives in the Library, so that is where the reader is.
        isActive={item.href === PROJECTS_PATHS.library || undefined}
        isDisabled={leavingTo !== null && leavingTo !== item.href}
        {...extra}
      />
    </Box>
  );

  return (
    <>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={close}
        placement="bottom-start"
        offset={LIST_OFFSET}
        // Presses outside close it through `closeOutside`, which lets the nav's portaled lists through.
        closeOnBlur={false}
        // Only a keyboard open moves the focus in, onto the first row.
        autoFocus={false}
      >
        {/* Wraps the Box because PopoverTrigger drops a tooltip's handlers before they reach the button. */}
        <Tooltip
          message="Navigation"
          keyboardShortcut={SIDEBAR_SHORTCUT}
          placement="bottom-start"
          isDisabled={isTooltipHeld}
          keepMountedWhileDisabled
        >
          <Box display="inline-flex">
            <PopoverTrigger>
              <IconButton
                ref={buttonRef}
                onPointerDown={() => setIsKeyboardOpen(false)}
                onKeyDown={(event) => {
                  if (OPENING_KEYS.has(event.key)) setIsKeyboardOpen(true);
                }}
                onPointerEnter={() => setIsTooltipHeld(false)}
                onPointerLeave={() => setIsTooltipHeld(false)}
                onBlur={() => setIsTooltipHeld(false)}
                aria-label="Navigation"
                icon={MarkIcon}
                shape="circle"
                variant="tertiary"
              />
            </PopoverTrigger>
          </Box>
        </Tooltip>
        <Portal>
          <NavPopoverContentAndBody
            ariaLabel="Navigation"
            width={72}
            // The nav's own surface, so its rows read as they do in the nav.
            bg="base.background"
          >
            <CompactNavContext.Provider value>
              <ExpandedNav>
                <Box px={2} pb={2}>
                  <WorkspaceSelector
                    switchWith={(go) => leaveWith(SWITCH_WORKSPACE, go)}
                    // Overhangs the popover, as in the prototype, so the list stands apart from the rows under it.
                    listWidth="xs"
                  />
                </Box>
                <Column
                  ref={listRefs}
                  // The shortcut closes this popover rather than yielding to it.
                  {...{ [MINI_NAV_SHEET_ATTRIBUTE]: "" }}
                  // The full AMP nav can run taller than a short window; the rows scroll inside it, below the workspace selector.
                  maxHeight="calc(100dvh - var(--chakra-sizes-24) - var(--chakra-sizes-12))"
                  overflowY="auto"
                  gap={2}
                  px={2}
                  sx={scrollFade.sx}
                >
                  <NavHoverProvider>
                    {/* Each group's own column holds the gaps between its rows, so the pill slides across them. */}
                    <Column gap={2}>
                      {lead.map((item) =>
                        leaveItem(item, cachedNavIcon(item.icon)),
                      )}
                      <NavItem
                        icon={cachedNavIcon(AlertingIcon)}
                        label="Inbox"
                        ariaLabel={unreadLabel("Inbox", unreadCount)}
                        rightElement={<UnreadBadge unread={unreadCount} />}
                        isDisabled={leavingTo !== null}
                        // The inbox opens over the canvas, which stays open with any editor on it.
                        onClick={() => {
                          close();
                          setIsInboxOpen(true);
                        }}
                      />
                      {[...area, ...more].map((item) =>
                        leaveItem(item, cachedNavIcon(item.icon)),
                      )}
                      {leaveItem(
                        {
                          href: PROJECTS_PATHS.projects,
                          label: TERMS.project.Plural,
                        },
                        cachedNavIcon(ProjectsIcon),
                      )}
                    </Column>
                  </NavHoverProvider>
                  <Box borderTop="1px solid" borderColor="base.divider" />
                  <NavHoverProvider>
                    <Column gap={2}>
                      <Box onClick={closeAfterResource}>
                        <Resources />
                      </Box>
                      {/* Pylon only loads in non-dev builds, as in the nav's footer. */}
                      {!import.meta.env.DEV && (
                        <Box onClick={close}>
                          <ChatSupport />
                        </Box>
                      )}
                      {context && leaveItem(context, ContextNavIcon)}
                      {settings && leaveItem(settings, SettingsNavIcon)}
                    </Column>
                  </NavHoverProvider>
                </Column>
              </ExpandedNav>
            </CompactNavContext.Provider>
          </NavPopoverContentAndBody>
        </Portal>
      </Popover>
      <InboxModal isOpen={isInboxOpen} onClose={() => setIsInboxOpen(false)} />
    </>
  );
}

/** The solid mark inset in its 20px slot as the line icons beside it are, so it doesn't read heavier than them. */
const MarkIcon = (props: SVGAttributes<SVGSVGElement>) => (
  <HightouchMark viewBox="-2 -2 28 28" {...props} />
);

/** Stands in a picked row's icon while the open editor saves. */
const SavingIcon = () => <Spinner size="sm" />;

/** The nav's own parts, with their labels shown although the nav is hidden behind the canvas. */
function ExpandedNav({ children }: { children: ReactNode }) {
  const navWidth = useNavWidth();
  return (
    <NavWidthContext.Provider value={{ ...navWidth, isNavOpen: true }}>
      {children}
    </NavWidthContext.Provider>
  );
}
